import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { Injectable, HostListener, Input, Directive, NgModule } from '@angular/core';
import * as i1 from '@angular/router';
import { ResolveStart, NavigationEnd, ResolveEnd } from '@angular/router';
import { Subject, filter } from 'rxjs';
import * as i2 from '@yourcause/common/currency';

var EventType;
(function (EventType) {
    EventType["Click"] = "Click";
    EventType["Navigation"] = "Navigation";
    EventType["ResolveRoute"] = "Resolve route";
})(EventType || (EventType = {}));

class AnalyticsService {
    constructor(router, moneyService) {
        this.router = router;
        this.moneyService = moneyService;
        this.events$ = new Subject();
        this.routerResolveMap = {};
    }
    /**
     *
     * @returns Observable stream to Analytics Events
     */
    getEvents() {
        this.events$.next(undefined);
        return this.events$;
    }
    /**
     *
     * @param event Analytics Event to Emit
     */
    emitEvent(event) {
        this.events$.next(event);
    }
    /**
     * Initiate Router Analytics Subscription
     */
    initRouterAnalytics() {
        this.router.events.pipe(filter((event) => {
            return event instanceof ResolveStart ||
                event instanceof NavigationEnd ||
                event instanceof ResolveEnd;
        })).subscribe((event) => {
            const eventToEmit = this.evaluateRouterEventForEmit(event);
            if (eventToEmit) {
                this.emitEvent(eventToEmit);
            }
        });
    }
    /**
     *
     * @param event Event to evaluate for emit
     * We track duration on Resolvers and record how long they took
     * We also track when a user navigates to a page
     */
    evaluateRouterEventForEmit(event) {
        let eventToEmit = {
            eventType: EventType.ResolveRoute,
            eventName: event.url,
            extras: {}
        };
        if (event instanceof ResolveStart) {
            const startTime = performance.now();
            this.routerResolveMap[event.id] = startTime;
            eventToEmit = undefined;
        }
        else if (event instanceof ResolveEnd) {
            const startTime = this.routerResolveMap[event.id];
            if (startTime) {
                const endTime = performance.now();
                const durationInSeconds = this.moneyService.toFixedString((endTime - startTime) / 1000, 2);
                eventToEmit.extras.duration = durationInSeconds;
            }
        }
        else if (event instanceof NavigationEnd) {
            eventToEmit.eventType = EventType.Navigation;
        }
        return eventToEmit;
    }
    /**
     *
     * @param eventName string that will show in Mixpanel
     * @param extras additional info we need to track
     */
    emitClick(eventName, extras) {
        this.emitEvent({
            eventName,
            eventType: EventType.Click,
            extras
        });
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: AnalyticsService, deps: [{ token: i1.Router }, { token: i2.MoneyService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: AnalyticsService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: AnalyticsService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: i1.Router }, { type: i2.MoneyService }] });

class TrackClickDirective {
    constructor(analyticsService) {
        this.analyticsService = analyticsService;
        /**
         * Defaults to click, but is an input so we can expand in the future
         */
        this.ycTrackClickType = EventType.Click;
    }
    trackEvent() {
        if (this.ycTrackClick) {
            this.analyticsService.emitEvent({
                eventType: this.ycTrackClickType,
                eventName: this.ycTrackClick,
                extras: this.ycTrackClickExtras
            });
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: TrackClickDirective, deps: [{ token: AnalyticsService }], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.2.10", type: TrackClickDirective, isStandalone: false, selector: "[ycTrackClick]", inputs: { ycTrackClick: "ycTrackClick", ycTrackClickExtras: "ycTrackClickExtras", ycTrackClickType: "ycTrackClickType" }, host: { listeners: { "mousedown": "trackEvent()", "keydown.enter": "trackEvent()" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: TrackClickDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[ycTrackClick]',
                    standalone: false
                }]
        }], ctorParameters: () => [{ type: AnalyticsService }], propDecorators: { ycTrackClick: [{
                type: Input
            }], ycTrackClickExtras: [{
                type: Input
            }], ycTrackClickType: [{
                type: Input
            }], trackEvent: [{
                type: HostListener,
                args: ['mousedown']
            }, {
                type: HostListener,
                args: ['keydown.enter']
            }] } });

class AnalyticsModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: AnalyticsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.10", ngImport: i0, type: AnalyticsModule, declarations: [TrackClickDirective], imports: [CommonModule], exports: [TrackClickDirective] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: AnalyticsModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: AnalyticsModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule
                    ],
                    declarations: [
                        TrackClickDirective
                    ],
                    exports: [
                        TrackClickDirective
                    ]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { AnalyticsModule, AnalyticsService, EventType, TrackClickDirective };
